import type {
  DeprecatedTabsContentOption,
  DeprecatedTabsTriggerOption,
} from "@v1/components/designSystem/tabs/Tabs";
import type { FormKey } from "@v1/contexts/ElementEditorErrorContext";
import type { ElementErrorType } from "@v1/utils/element";
import type { StringMetafield } from "replo-runtime/shared/types";
import type { ReploElementType } from "schemas/generated/element";

import * as React from "react";

import Editor, { useMonaco } from "@monaco-editor/react";
import { Separator } from "@replo/design-system/components/separator/Separator";
import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import InlineAlert from "@v1/components/designSystem/alert/InlineAlert";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { DeprecatedTabs } from "@v1/components/designSystem/tabs/Tabs";
import { useElementEditorDataContext } from "@v1/contexts/ElementEditorDataContext";
import { useElementEditorErrorContext } from "@v1/contexts/ElementEditorErrorContext";
import { useInitial } from "@v1/hooks/useInitial";
import useOnChangeMetafields from "@v1/hooks/useOnChangeMetafields";
import { preprocessHtmlForXmlValidation } from "@v1/utils/html";
import validatePageModalPath from "@v1/utils/validatePageModalPath";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { workspaceUtils } from "@v2/trpc/routes/workspace";
import debounce from "lodash-es/debounce";
import { slugify } from "replo-utils/lib/string";

import ElementCustomCssEditor from "./ElementCustomCssEditor";
import { ElementEditorErrors } from "./ElementEditorErrors";
import ElementNameEditor from "./ElementNameEditor";
import SeoElementEditor from "./SeoElementEditor";
import { ShopifyThemeSettings } from "./ShopifyThemeSettings";

type PageElementEditorTab = "general" | "shopifyTheme" | "advanced";

const PAGE_ELEMENT_EDITOR_TABS: DeprecatedTabsTriggerOption[] = [
  { value: "general", label: "General" },
  { value: "shopifyTheme", label: "Shopify Theme" },
  { value: "advanced", label: "Advanced" },
];

const PageElementEditor: React.FC = () => {
  const [selectedTab, setSelectedTab] =
    React.useState<PageElementEditorTab>("general");

  const PAGE_ELEMENT_EDITOR_CONTENT: DeprecatedTabsContentOption[] = [
    { value: "general", tabContent: <PageBasicSettings /> },
    {
      value: "shopifyTheme",
      tabContent: <ShopifyThemeSettings />,
    },
    { value: "advanced", tabContent: <PageAdvancedSettings /> },
  ];

  return (
    <div className="flex h-full">
      <DeprecatedTabs
        tabsOptions={PAGE_ELEMENT_EDITOR_TABS}
        contentOptions={PAGE_ELEMENT_EDITOR_CONTENT}
        value={selectedTab}
        onValueChange={(value) => {
          setSelectedTab(value as PageElementEditorTab);
        }}
        size="sm"
        defaultValue={selectedTab}
      />
    </div>
  );
};

const PageBasicSettings: React.FC = () => {
  const { element, onChangeElement } = useElementEditorDataContext();
  const initialElementIsPublished = useInitial(element.isPublished);
  const { errorMapping, setErrors, clearErrors } =
    useElementEditorErrorContext();
  const logEvent = useLogAnalytics();

  const { metafields, onMetafieldChange } = useOnChangeMetafields(
    element,
    onChangeElement,
  );

  const handleNameChange = (name: string) => {
    const shopifyPagePath = !element?.shopifyPagePath
      ? slugify(name)
      : element.shopifyPagePath;
    if (errorMapping["path"]?.includes("pathAlreadyExists")) {
      clearErrors("path", ["pathAlreadyExists"]);
    }
    onChangeElement({
      ...element,
      name,
      shopifyPagePath,
    });
  };

  const handlePathChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const shopifyPagePath = e.target.value;

    if (shopifyPagePath.length === 0) {
      setErrors("path", ["pathIsEmpty"]);
    } else {
      clearErrors("path", ["pathIsEmpty"]);
      if (validatePageModalPath(shopifyPagePath)) {
        setErrors("path", ["isInvalidPathName"]);
      } else {
        clearErrors("path", ["isInvalidPathName"]);
      }
    }

    if (errorMapping["path"]?.includes("pathAlreadyExists")) {
      clearErrors("path", ["pathAlreadyExists"]);
    }
    if (errorMapping["path"]?.includes("pathIsEmpty")) {
      clearErrors("path", ["pathIsEmpty"]);
    }

    onChangeElement({
      ...element,
      shopifyPagePath,
    });

    await workspaceUtils.getAllElementsWithShopifyUrl.invalidate();
  };

  const routes = [
    {
      value: "pages/",
      isSelected: true,
    },
  ];

  return (
    <div className="flex flex-col gap-y-4">
      <ElementNameEditor
        type="page"
        inputName="page-name"
        initialName={element.name}
        onChange={handleNameChange}
      />

      <DeprecatedLabeledControl
        label="URL"
        id="page-data-path"
        error={
          <PathInputError
            errorMapping={errorMapping}
            elementType={element.type}
          />
        }
        size="sm"
      >
        <div className="flex flex-row items-center gap-2">
          <div className="flex-none">
            <DeprecatedInput
              value={routes.find((route) => route.isSelected)?.value}
              size="sm"
              layoutClassName="w-16"
              isDisabled={true}
            />
          </div>
          <DeprecatedInput
            id="page-data-path"
            value={element.shopifyPagePath}
            placeholder="path"
            size="sm"
            onChange={(e) => void handlePathChange(e)}
          />
        </div>
      </DeprecatedLabeledControl>

      {initialElementIsPublished && (
        <SwitchWithDescription
          label="Is Published"
          description="Public users can see this page."
          isOn={element.isPublished}
          onChange={(isPublished) => {
            return onChangeElement({ ...element, isPublished });
          }}
          size="sm"
          variant="card"
        />
      )}

      <SwitchWithDescription
        label="Set As Homepage"
        description="Set this page as the homepage of your store."
        isOn={element.isHomepage}
        onChange={(isHomepage) => {
          logEvent("editor.element.update.homepage", { isHomepage });
          onChangeElement({ ...element, isHomepage });
        }}
        size="sm"
        variant="card"
      />

      <Separator />

      <SeoElementEditor
        isPageSettings={true}
        metafields={metafields}
        onMetafieldChange={onMetafieldChange}
      />
    </div>
  );
};

const PathInputError: React.FC<{
  errorMapping: Record<FormKey, ElementErrorType[] | null>;
  elementType: ReploElementType;
}> = ({ errorMapping, elementType }) => {
  const isError =
    errorMapping &&
    Object.keys(errorMapping).some((key) => {
      return errorMapping[key as FormKey]?.some((error) => {
        return [
          "pathAlreadyExists",
          "isInvalidPathName",
          "pathIsEmpty",
          "loadingMissingData",
          "errorLoadingData",
          "storeHasNoBlogs",
        ].includes(error);
      });
    });

  if (isError) {
    const errors = Object.values(errorMapping).flat();
    const primaryErrorType = errors.find((error) => {
      return error?.toLowerCase().includes("path") && typeof error === "string";
    });
    const errorType = primaryErrorType ?? errors[0];
    if (errorType) {
      return (
        <ElementEditorErrors errorType={errorType} elementType={elementType} />
      );
    }
  }

  return null;
};

const PageAdvancedSettings: React.FC = () => {
  const { setErrors: setElementErrors, clearErrors } =
    useElementEditorErrorContext();
  const { element, onChangeElement } = useElementEditorDataContext();
  const { metafields, onMetafieldChange } = useOnChangeMetafields(
    element,
    onChangeElement,
  );
  const [htmlErrors, setHtmlErrors] = React.useState<string[]>([]);

  const monaco = useMonaco();
  monaco?.editor.defineTheme("replo", {
    base: "vs",
    colors: {
      "editor.background": "#f5f5f5", // neutral-100
      "editor.lineHighlightBackground": "#f1f5f9",
    },
    inherit: true,
    rules: [],
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Disable exhaustive deps for now
  const handleHeadContentChange = React.useCallback(
    debounce((value: string) => {
      const { preprocessedHtml } = preprocessHtmlForXmlValidation(value);

      const parser = new DOMParser();
      const doc = parser.parseFromString(
        `<html>${preprocessedHtml}</html>`,
        "application/xml",
      );
      const errors = Array.from(doc.querySelectorAll("parsererror > div"))
        .map((el) => el.innerHTML)
        // NOTE (Evan, 9/25/23) Omitting the closing meta tag is very common,
        // so we hide associated errors to avoid confusion.
        .filter(
          (error) => !error.includes("Opening and ending tag mismatch: meta"),
        );
      setHtmlErrors(errors);
      if (errors.length > 0) {
        setElementErrors("customHtml", ["invalidCustomHtml"]);
      } else {
        clearErrors("customHtml", ["invalidCustomHtml"]);
      }
      onChangeElement((element) => ({
        ...element,
        customHeadContent: value,
      }));
    }, 300),
    [onMetafieldChange, debounce],
  );

  const beforeHeadEndContent =
    // NOTE (Gabe 2023-11-08): This fallback is where we used to store the
    // customHead. It no longer exists in the schema.
    // @ts-expect-error
    element.customHeadContent ??
    (
      metafields?.find((field) => field.key === "before_head_end_content") as
        | StringMetafield
        | undefined
    )?.value;

  return (
    <div className="flex flex-col gap-y-4">
      <DeprecatedLabeledControl
        label="Custom Page <head>"
        id="page-data-name"
        size="sm"
      >
        <p className="text-neutral text-xs">
          {
            "This content will be inserted before the closing </head> tag in your Shopify theme."
          }
        </p>
        <div
          className="flex flex-col gap-2 overflow-hidden rounded-sm"
          data-testid="custom-head-editor"
        >
          <Editor
            height="200px"
            defaultLanguage="html"
            defaultValue={beforeHeadEndContent}
            onChange={(value) => handleHeadContentChange(String(value))}
            options={{
              minimap: { enabled: false },
              // NOTE (Noah, 2026-02-15, REPL-23631): Disable occurrence highlighting
              // because Monaco internally constructs a regex from the text under
              // the cursor, which crashes on content with regex metacharacters
              // (e.g. CSS `* { margin: 0; }` inside a <style> tag).
              occurrencesHighlight: false,
              scrollbar: { verticalScrollbarSize: 0 },
              lineNumbers: "off",
              lineDecorationsWidth: 0,
              padding: { top: 16 },
            }}
            theme="replo"
          />
        </div>
        {htmlErrors.length > 0 && (
          <InlineAlert variant="error" multiline>
            <div className="flex flex-col gap-2">
              <p>
                Replo detected HTML which may be invalid. This may cause issues
                after this page is published. If you see this page not working
                correctly, please reach out to support@replo.app with any
                questions.
              </p>
              <ul>
                {htmlErrors.map((error) => (
                  <li
                    key={error}
                    className="ml-4"
                    style={{ listStyleType: "circle" }}
                  >
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          </InlineAlert>
        )}
      </DeprecatedLabeledControl>
      <ElementCustomCssEditor />
    </div>
  );
};

export default PageElementEditor;
